import { useState } from 'react';
import { Calendar, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import type { ComboboxOption } from './combobox';

export const DATE_RANGE_OPTIONS: ComboboxOption[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'last_365_days', label: 'Last 365 days' },
  { value: 'last_4_weeks', label: 'Last 4 weeks (L4W)' },
  { value: 'last_8_weeks', label: 'Last 8 weeks (L8W)' },
  { value: 'last_13_weeks', label: 'Last 13 weeks (L13W)' },
  { value: 'last_26_weeks', label: 'Last 26 weeks (L26W)' },
  { value: 'last_52_weeks', label: 'Last 52 weeks (L52W)' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'this_year', label: 'This year' },
  { value: 'last_year', label: 'Last year' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

export const FISCAL_DATE_RANGE_OPTIONS: ComboboxOption[] = [
  { value: 'this_fiscal_month', label: 'This fiscal month' },
  { value: 'last_fiscal_month', label: 'Last fiscal month' },
  { value: 'this_fiscal_quarter', label: 'This fiscal quarter' },
  { value: 'last_fiscal_quarter', label: 'Last fiscal quarter' },
  { value: 'this_fiscal_half', label: 'This fiscal half' },
  { value: 'last_fiscal_half', label: 'Last fiscal half' },
  { value: 'this_fiscal_year', label: 'This fiscal year' },
  { value: 'last_fiscal_year', label: 'Last fiscal year' },
];

// Map backend date range values to frontend format
export const mapDateRangeFromBackend = (dateRange?: string): string | undefined => {
  if (!dateRange) return undefined;
  const mapping: Record<string, string> = {
    'TODAY': 'today',
    'YESTERDAY': 'yesterday',
    'LAST_7_DAYS': 'last_7_days',
    'LAST_30_DAYS': 'last_30_days',
    'LAST_90_DAYS': 'last_90_days',
    'LAST_365_DAYS': 'last_365_days',
    'LAST_4_WEEKS': 'last_4_weeks',
    'LAST_8_WEEKS': 'last_8_weeks',
    'LAST_13_WEEKS': 'last_13_weeks',
    'LAST_26_WEEKS': 'last_26_weeks',
    'LAST_52_WEEKS': 'last_52_weeks',
    'THIS_MONTH': 'this_month',
    'LAST_MONTH': 'last_month',
    'THIS_QUARTER': 'this_quarter',
    'LAST_QUARTER': 'last_quarter',
    'THIS_YEAR': 'this_year',
    'LAST_YEAR': 'last_year',
    'ALL_TIME': 'all_time',
    'CUSTOM': 'custom',
    'THIS_FISCAL_MONTH': 'this_fiscal_month',
    'LAST_FISCAL_MONTH': 'last_fiscal_month',
    'THIS_FISCAL_QUARTER': 'this_fiscal_quarter',
    'LAST_FISCAL_QUARTER': 'last_fiscal_quarter',
    'THIS_FISCAL_HALF': 'this_fiscal_half',
    'LAST_FISCAL_HALF': 'last_fiscal_half',
    'THIS_FISCAL_YEAR': 'this_fiscal_year',
    'LAST_FISCAL_YEAR': 'last_fiscal_year',
  };
  return mapping[dateRange] || dateRange.toLowerCase();
};

// Map frontend date range values to backend format
export const mapDateRangeToBackend = (dateRange: string): string => {
  const mapping: Record<string, string> = {
    'today': 'TODAY',
    'yesterday': 'YESTERDAY',
    'last_7_days': 'LAST_7_DAYS',
    'last_30_days': 'LAST_30_DAYS',
    'last_90_days': 'LAST_90_DAYS',
    'last_365_days': 'LAST_365_DAYS',
    'last_4_weeks': 'LAST_4_WEEKS',
    'last_8_weeks': 'LAST_8_WEEKS',
    'last_13_weeks': 'LAST_13_WEEKS',
    'last_26_weeks': 'LAST_26_WEEKS',
    'last_52_weeks': 'LAST_52_WEEKS',
    'this_month': 'THIS_MONTH',
    'last_month': 'LAST_MONTH',
    'this_quarter': 'THIS_QUARTER',
    'last_quarter': 'LAST_QUARTER',
    'this_year': 'THIS_YEAR',
    'last_year': 'LAST_YEAR',
    'all_time': 'ALL_TIME',
    'custom': 'CUSTOM',
    'this_fiscal_month': 'THIS_FISCAL_MONTH',
    'last_fiscal_month': 'LAST_FISCAL_MONTH',
    'this_fiscal_quarter': 'THIS_FISCAL_QUARTER',
    'last_fiscal_quarter': 'LAST_FISCAL_QUARTER',
    'this_fiscal_half': 'THIS_FISCAL_HALF',
    'last_fiscal_half': 'LAST_FISCAL_HALF',
    'this_fiscal_year': 'THIS_FISCAL_YEAR',
    'last_fiscal_year': 'LAST_FISCAL_YEAR',
  };
  return mapping[dateRange] || dateRange.toUpperCase();
};

// Format date string (YYYY-MM-DD) to MM/DD/YYYY
export const formatDateForDisplay = (dateString: string | undefined): string | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return null;
  }
};

interface DateRangePickerProps {
  value: string;
  customDateStart?: string;
  customDateEnd?: string;
  onChange: (value: string, customStart?: string, customEnd?: string) => void;
  disabled?: boolean;
  showFiscalOptions?: boolean;
  /** Pre-filtered options list. When provided, overrides showFiscalOptions. */
  availableOptions?: ComboboxOption[];
}

export function DateRangePicker({
  value,
  customDateStart,
  customDateEnd,
  onChange,
  disabled,
  showFiscalOptions = false,
  availableOptions,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  const allOptions = availableOptions
    ? availableOptions
    : showFiscalOptions
      ? [...DATE_RANGE_OPTIONS, ...FISCAL_DATE_RANGE_OPTIONS]
      : DATE_RANGE_OPTIONS;

  const fiscalValues = new Set(FISCAL_DATE_RANGE_OPTIONS.map((o) => o.value));
  const standardOptions = allOptions.filter((o) => !fiscalValues.has(o.value));
  const fiscalOptions = allOptions.filter((o) => fiscalValues.has(o.value));

  const displayLabel = (() => {
    if (value === 'custom' && customDateStart && customDateEnd) {
      const startFormatted = formatDateForDisplay(customDateStart);
      const endFormatted = formatDateForDisplay(customDateEnd);
      if (startFormatted && endFormatted) {
        return `${startFormatted} - ${endFormatted}`;
      }
    }
    return allOptions.find(opt => opt.value === value)?.label || 'Select date range';
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2" disabled={disabled}>
          <Calendar className="h-4 w-4" />
          {displayLabel}
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Date Range</label>
            <Command shouldFilter>
              <CommandInput placeholder="Search date range..." />
              <CommandList>
                <CommandEmpty>No date range found.</CommandEmpty>
                <CommandGroup>
                  {standardOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      disabled={disabled}
                      onSelect={() => {
                        if (!disabled) {
                          if (option.value !== 'custom') {
                            onChange(option.value);
                            setOpen(false);
                          } else {
                            onChange(option.value, customDateStart, customDateEnd);
                          }
                        }
                      }}
                    >
                      <div className="mr-2 h-4 w-4 shrink-0 flex items-center justify-center">
                        <Check
                          className={`h-4 w-4 ${
                            value === option.value ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      </div>
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {fiscalOptions.length > 0 && (
                  <CommandGroup heading="Fiscal Periods">
                    {fiscalOptions.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        disabled={disabled}
                        onSelect={() => {
                          if (!disabled) {
                            onChange(option.value);
                            setOpen(false);
                          }
                        }}
                      >
                        <div className="mr-2 h-4 w-4 shrink-0 flex items-center justify-center">
                          <Check
                            className={`h-4 w-4 ${
                              value === option.value ? 'opacity-100' : 'opacity-0'
                            }`}
                          />
                        </div>
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </div>
          {value === 'custom' && (
            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Start Date</label>
                <Input
                  type="date"
                  value={customDateStart || ''}
                  onChange={(e) => onChange('custom', e.target.value, customDateEnd)}
                  disabled={disabled}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">End Date</label>
                <Input
                  type="date"
                  value={customDateEnd || ''}
                  onChange={(e) => onChange('custom', customDateStart, e.target.value)}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
